/**
 * `7459` — *{slack, connection} {notify, message} in {channel, slackChannel}*, and the three
 * `static` Slack recipes that predate the new builder.
 *
 * Evidence: 8 production nodes carry this block, all with the same three slots —
 * `connection: {authProvider, connectionId, appId, invalid}`,
 * `slackChannel: {value: "C0AJ3NB1LJF", title: "store-updates"}` and a token-bearing `message`
 * (recipes 127462551, 166546365/801/931/086, 166555471, 180570654, 208361731). The static
 * recipes `38`/`40`/`96`/`98` declare the same `slackChannel` picker
 * (`{type: 'list', remote: {app: 'slack', type: 'channels'}}`) and `801`/`802` the `users`
 * variant — which is the same Slack call, because `chat.postMessage`'s `channel` argument takes
 * a user id and opens the DM itself.
 *
 * **Nothing here holds a credential.** `connection.connectionId` is monday's integer credential
 * id, preserved for the diff only; the bot token comes from this account's own Slack connection,
 * resolved inside the worker. With no connection the block throws `AutomationNotConnected` and
 * the engine parks the automation in monday's own `not_connected` state.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { AutomationNotConfigured, AutomationNotConnected, connectionRefSchema, slackTargetSchema } from '../integrations.js';
import { interpolateTokens } from '../tokens.js';
import { tokenContextFor } from './token-context.js';
export const slackNotifyParams = z.object({
    connection: connectionRefSchema,
    slackChannel: slackTargetSchema,
    /** The `801`/`802` slot; the same call, a user id instead of a channel id. */
    slackUser: slackTargetSchema,
    message: z.string().default(''),
});
/** Which Slack conversation the message goes to, and under which slot name. */
export function slackDestination(params) {
    if (params.slackChannel !== null)
        return { slot: 'slackChannel', id: params.slackChannel.value };
    if (params.slackUser !== null)
        return { slot: 'slackUser', id: params.slackUser.value };
    return null;
}
const slackBlock = (id, sentencePartial) => defineAction({
    id,
    kind: 'action',
    app: 'slack',
    sentencePartial,
    paramsSchema: slackNotifyParams,
    executable: true,
    requiredApp: 'slack',
    references: () => [{ kind: 'app', id: 'slack' }],
    async execute(context, params) {
        const integrations = context.services.integrations;
        if (integrations === undefined || !(await integrations.isConnected('slack'))) {
            throw new AutomationNotConnected('slack');
        }
        const destination = slackDestination(params);
        if (destination === null)
            throw new AutomationNotConfigured('slackChannel');
        const tokens = await tokenContextFor(context, [params.message]);
        const text = interpolateTokens(params.message, tokens);
        const result = await integrations.sendSlackMessage({ channel: destination.id, text });
        if (!result.ok)
            throw new Error(`slack chat.postMessage failed (${result.status}): ${result.detail ?? 'no detail'}`);
        return { channel: destination.id, slot: destination.slot, text, status: result.status, attempts: result.attempts };
    },
    /** Slack rate-limits with 429 and fails transiently with 5xx; both are worth one more try. */
    retryable: (error) => /\((429|5\d\d)\)/.test(error instanceof Error ? error.message : ''),
});
export const slackNotifyChannelAction = slackBlock('7459', '{slack,connection} {notify, message} in {channel, slackChannel}');
/** The `801`/`802` sentence. Same block, different placeholder — monday publishes no id for it. */
export const slackNotifyUserAction = slackBlock('ez.a.slack_notify_user', '{notify, message} {user, slackUser}');
/** Recipe `98`: *When any update is posted, send it to {channel, slackChannel}* — the update
 *  body is the message, so the `message` slot is filled from the trigger's `{update.preview}`. */
export const slackSendUpdateAction = slackBlock('ez.a.slack_send_update', 'send it to {channel, slackChannel}');
//# sourceMappingURL=7459.js.map